import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState, useRef } from "react";
import { createRoot } from "react-dom/client";
import { flushSync } from "react-dom";
import { sessionExpired, watchSession } from "./session-guard";
import { sessionRecovery } from "./session-recovery";
import { api, get, put, remove, entries } from "./store";
import { drainQueue } from "./synchronisation";
import { restoreQueueState } from "./queue-state";
import { parseCalculator, sumParts, editablePayload, countSchema, } from "../domain";
import "./style.css";
import { AccessAdmin, SecuritySetup } from "./security";
import { Login } from "./login";
import { CountInput } from "./count-input";
const recovery = sessionRecovery(localStorage, { get, put });
const now = () => new Date().toISOString();
const uid = () => crypto.randomUUID();
const fieldLabel = (key) => ({
    slip: "Customer slip number",
    assistant: "Van assistant",
    km: "KM reading",
    specialRequest: "Special laundry request",
    startedAt: "Started",
    finishedAt: "Finished",
    contractException: "contract exception",
    exceptionCharges: "exception charges",
})[key] ??
    (key.charAt(0).toUpperCase() + key.slice(1)).replace(/([a-z])([A-Z])/g, "$1 $2");
function Field({ label, children, }) {
    return (_jsxs("label", { className: "field", children: [_jsx("span", { children: label }), children] }));
}
function App() {
    const [auth, setAuth] = useState(null), [rows, setRows] = useState([]), [staff, setStaff] = useState([]), [page, setPage] = useState("Overview"), [error, setError] = useState(""), [notice, setNotice] = useState(""), [online, setOnline] = useState(navigator.onLine), [reachable, setReachable] = useState(true), [pending, setPending] = useState([]), [busy, setBusy] = useState(false), [detail, setDetail] = useState(null), [editing, setEditing] = useState(null);
    const authRef = useRef(auth);
    authRef.current = auth;
    const runningSync = useRef(null);
    const submitting = useRef(false);
    const actor = auth?.actor;
    function lockSession() {
        recovery.lock();
        sessionStorage.removeItem("cc-active-session");
        authRef.current = null;
        setAuth(null);
        setRows([]);
        setStaff([]);
        setPending([]);
        setDetail(null);
        setEditing(null);
        setNotice("");
        setError("");
    }
    function currentSession() {
        if (!auth || authRef.current !== auth)
            return false;
        if (sessionExpired(auth.expiresAt)) {
            lockSession();
            return false;
        }
        return true;
    }
    useEffect(() => {
        if (!auth)
            return;
        return watchSession({ expiresAt: auth.expiresAt, window, document,
            expire: (immediate) => {
                if (authRef.current !== auth)
                    return;
                if (immediate)
                    flushSync(lockSession);
                else
                    lockSession();
            },
        });
    }, [auth]);
    const configs = rows.filter((r) => r.kind === "config");
    const can = (roles) => !!actor && roles.includes(actor.role);
    async function refresh() {
        if (!currentSession() || !auth || auth.purpose !== "full")
            return;
        const data = await api("/snapshot").catch((e) => {
            setReachable(false);
            if (e.status === 401 && authRef.current === auth) {
                recovery.lock();
                sessionStorage.removeItem("cc-active-session");
                authRef.current = null;
                setAuth(null);
                setRows([]);
                setStaff([]);
                setPending([]);
            }
            throw e;
        });
        if (!currentSession())
            return;
        setReachable(true);
        setRows(data.records);
        setStaff(data.staff);
        await put("snapshot:" + auth.actor.id, data);
        await queueState();
    }
    async function queueState() {
        if (auth?.purpose === "full")
            await restoreQueueState(() => entries("queue:" + auth.actor.id + ":"), () => currentSession(), setPending);
    }
    useEffect(() => {
        const on = () => setOnline(navigator.onLine);
        window.addEventListener("online", on);
        window.addEventListener("offline", on);
        let disposed = false;
        if (sessionStorage.getItem("cc-signed-out") !== "true" && !recovery.blocked())
            api("/session")
                .then(async (a) => {
                if (disposed)
                    return;
                await recovery.remember(a);
                if (!disposed)
                    setAuth(a);
            })
                .catch(async (e) => {
                if (disposed)
                    return;
                if (e.status) {
                    recovery.lock();
                    return;
                }
                try {
                    const saved = await recovery.restore();
                    if (saved && !disposed)
                        setAuth(saved);
                }
                catch {
                    setError("Could not restore offline access. Keep saved device data intact and reconnect to sign in.");
                }
            });
        if ("serviceWorker" in navigator)
            navigator.serviceWorker.register("/sw.js").catch(() => { });
        return () => {
            disposed = true;
            window.removeEventListener("online", on);
            window.removeEventListener("offline", on);
        };
    }, []);
    useEffect(() => {
        if (auth?.purpose === "full") {
            setPage(auth.actor.role === "admin" ? "Access Administration" : "Overview");
            setDetail(null);
            setEditing(null);
            setError("");
            setNotice("");
            queueState().catch(() => {
                if (authRef.current === auth)
                    setError("Could not read saved submissions. Keep this device's storage intact and contact your supervisor.");
            });
            get("snapshot:" + auth.actor.id).then((c) => {
                if (c && currentSession()) {
                    setRows(c.records);
                    setStaff(c.staff);
                }
            });
            refresh().catch((e) => {
                if (authRef.current === auth)
                    setError(e.status ? e.message :
                        "Server unavailable. Showing saved data; local work is preserved.");
            });
        }
    }, [auth]);
    useEffect(() => {
        if (auth?.purpose === "full" && online) {
            sync();
            const timer = setInterval(() => sync().catch(() => { }), 15000);
            return () => clearInterval(timer);
        }
    }, [online, auth]);
    async function sync() {
        if (!currentSession() || !auth || auth.purpose !== "full")
            return;
        if (runningSync.current)
            return runningSync.current;
        const run = async () => {
            const result = await drainQueue({
                read: () => entries("queue:" + auth.actor.id + ":"),
                send: (c) => {
                    if (!currentSession())
                        throw new Error("Session changed; work retained");
                    return api("/commands", c, auth.csrf);
                },
                remove,
                save: (e) => put(e.key, e.value),
            });
            if (!currentSession())
                return result;
            if (result.paused?.status === 401 || result.paused?.status === 403)
                setError("Sign in again to synchronise. Your local work is preserved.");
            if (result.paused && !result.paused.status)
                setReachable(false);
            await queueState();
            try {
                await refresh();
            }
            catch { }
            return result;
        };
        // Browser-wide lock serialises uploads from multiple tabs. Server request IDs
        // remain the correctness boundary if a browser lacks this optional API.
        const task = navigator.locks
            ? navigator.locks.request("cc-sync:" + auth.actor.id, run)
            : run();
        runningSync.current = task;
        try {
            return await task;
        }
        finally {
            runningSync.current = null;
        }
    }
    async function submit(command) {
        if (!currentSession() || !auth || auth.purpose !== "full" || submitting.current)
            return;
        submitting.current = true;
        setBusy(true);
        setError("");
        setNotice("");
        try {
            const key = "queue:" + auth.actor.id + ":" + command.requestId;
            await put(key, {
                command,
                queuedAt: Date.now(),
                state: "Saved on device",
            });
            await queueState();
            if (navigator.onLine) {
                // Wait for any existing drain, then include this newly persisted entry.
                if (runningSync.current)
                    await runningSync.current;
                const outcome = await sync();
                const own = outcome?.results.get(command.requestId);
                const retained = await get(key);
                if (own?.error || retained?.state === "Needs resolution")
                    throw new Error(own?.error ?? retained.error);
                if (retained) {
                    const message = outcome?.paused ? "Saved on this device; synchronisation paused. Do not re-enter this work." :
                        "Saved on this device; waiting for earlier work to be reviewed. Do not re-enter this count.";
                    setNotice(message);
                    return { local: true, message };
                }
                if (!retained) {
                    setNotice("Saved and synced");
                    return own ?? { synced: true };
                }
            }
            const message = "Saved on device, awaiting connection. Do not re-enter this work.";
            setNotice(message);
            return { local: true, message };
        }
        catch (e) {
            setError(e.message);
            return null;
        }
        finally {
            submitting.current = false;
            setBusy(false);
        }
    }
    function command(kind, customer, siteId, payload, old, reason = "") {
        return {
            requestId: uid(),
            entityId: old?.id ?? uid(),
            kind,
            customerId: customer.id,
            siteId,
            configId: old?.config_id ?? customer.id,
            configVersion: old?.config_version ?? customer.version,
            expectedVersion: old?.version ?? 0,
            occurredAt: now(),
            reason,
            payload,
        };
    }
    const counts = rows.filter((r) => r.kind === "count"), receipts = rows.filter((r) => r.kind === "receipt"), sorts = rows.filter((r) => r.kind === "sorting");
    const name = (r) => configs.find((c) => c.id === r.customer_id)?.payload.name ?? r.customer_id;
    async function history(row) {
        try {
            setDetail(await api("/history/" + row.id));
        }
        catch (e) {
            setError(e.message);
        }
    }
    if (!auth)
        return (_jsx(Login, { onLogin: async (a) => {
                await recovery.remember(a);
                sessionStorage.removeItem("cc-signed-out");
                setAuth(a);
            } }));
    const signOut = async () => {
        recovery.lock();
        sessionStorage.setItem("cc-signed-out", "true");
        sessionStorage.removeItem("cc-active-session");
        authRef.current = null;
        setAuth(null);
        setRows([]);
        setStaff([]);
        setPending([]);
        try {
            await api("/logout", {}, auth.csrf);
        }
        catch { }
    };
    if (auth.purpose !== "full")
        return _jsx(SecuritySetup, { auth: auth, onDone: signOut, onSignOut: signOut });
    const tabs = actor?.role === "admin"
        ? ["Access Administration", "My Security"]
        : [
            "Overview",
            ...(can(["backoffice"]) ? ["Customer Setup"] : []),
            ...(can(["counter", "supervisor", "backoffice"])
                ? ["Soiled Count"]
                : []),
            ...(can(["receiving", "supervisor", "backoffice"])
                ? ["Soiled Linen Receipt"]
                : []),
            ...(can(["sorting", "supervisor", "backoffice"])
                ? ["Soil Sorting"]
                : []),
            ...(can(["receiving", "sorting", "supervisor", "backoffice"])
                ? ["Verification"]
                : []),
            ...(can(["supervisor", "backoffice"]) ? ["Attention"] : []),
            "History",
            "My Security",
        ];
    return (_jsxs("div", { className: "app", children: [_jsxs("aside", { children: [_jsxs("a", { className: "brand", href: "#", onClick: () => setPage("Overview"), children: [_jsx("span", { className: "brand-mark", children: "C" }), _jsxs("span", { children: ["CORALCLEAN", _jsx("small", { children: "LINEN OPERATIONS" })] })] }), _jsx("p", { className: "nav-label", children: "WORKSPACE" }), _jsx("nav", { children: tabs.map((t) => (_jsxs("button", { className: page === t ? "selected" : "", onClick: () => {
                                setPage(t);
                                setError("");
                                setNotice("");
                            }, children: [_jsx("span", { children: {
                                        Overview: "◈",
                                        "Customer Setup": "▦",
                                        "Soiled Count": "▤",
                                        "Soiled Linen Receipt": "↓",
                                        "Soil Sorting": "⇄",
                                        Verification: "✓",
                                        Attention: "!",
                                        History: "◷",
                                        "Access Administration": "⚿",
                                        "My Security": "⚿",
                                    }[t] }), t] }, t))) }), _jsxs("div", { className: "sidebar-bottom", children: [_jsx("span", { className: "pill", children: "PHASE 1 \u00B7 REVIEW BUILD" }), _jsxs("p", { children: ["Synthetic demonstration data.", _jsx("br", {}), "Not connected to live operations."] })] })] }), _jsxs("main", { children: [_jsxs("header", { children: [_jsxs("div", { children: [_jsxs("span", { className: "eyebrow", children: ["CORALCLEAN /", " ", actor?.role === "portal" ? "CUSTOMER PORTAL" : "OPERATIONS"] }), _jsx("h1", { children: page === "Overview" ? "A clear view of your linen." : page })] }), _jsxs("div", { className: "user", children: [_jsx("span", { className: "connection " + (!(online && reachable) ? "offline" : ""), children: online && reachable
                                            ? "● Connected"
                                            : "● Server unavailable · local capture" }), _jsx("strong", { children: actor?.name }), _jsx("button", { className: "text-button", onClick: signOut, children: "Sign out" })] })] }), error && (_jsx("div", { role: "alert", className: "alert", children: error })), notice && (_jsx("div", { role: "status", className: "notice", children: notice })), pending.length > 0 && (_jsxs("div", { className: "queue", children: [_jsxs("strong", { children: [pending.length, " local submission", pending.length === 1 ? "" : "s"] }), _jsxs("span", { children: [pending.filter((p) => p.value.state === "Needs resolution")
                                        .length, " ", "need review"] }), _jsx("button", { onClick: sync, children: "Sync now" }), pending.map((p) => (_jsxs("details", { children: [_jsxs("summary", { children: [p.value.command.kind, " \u00B7 ", p.value.state] }), _jsx("p", { children: p.value.error ?? "Awaiting synchronisation or review of earlier work. Do not enter it again." }), _jsx("p", { children: p.value.command.payload.lines?.map((line) => {
                                            const name = configs.find(c => c.id === p.value.command.configId)?.payload.items.find((i) => i.id === line.itemId)?.name ?? "Item";
                                            return `${name}: ${line.parts?.length ? sumParts(line.parts) : "not entered"}${line.estimated != null ? `; estimated ${line.estimated}` : ""}`;
                                        }).join(" · ") }), p.value.command.kind === "count" && p.value.state !== "Retained retry" &&
                                        _jsx(RetryReview, { entry: p, rows: rows, actor: actor, busy: busy, onRetain: async (originalId, reason) => {
                                                if (!auth || !navigator.locks)
                                                    throw Error("Use a browser supporting safe queue locking for recovery.");
                                                setBusy(true);
                                                try {
                                                    await navigator.locks.request("cc-sync:" + auth.actor.id, async () => {
                                                        const group = (await entries("queue:" + auth.actor.id + ":")).filter(e => e.value.command.entityId === p.value.command.entityId && e.value.state !== "Retained retry");
                                                        if (!group.length)
                                                            throw Error("Queue changed. Refresh the list before reviewing.");
                                                        await api("/count-retries/retain", { originalId, reason, entries: group.map(e => e.value) }, auth.csrf);
                                                        for (const e of group)
                                                            await put(e.key, { ...e.value, state: "Retained retry", originalId, reason,
                                                                error: "Preserved as a retry; no additional linen counted." });
                                                    });
                                                    await queueState();
                                                    setNotice("Retry evidence retained. The original saved count is unchanged.");
                                                }
                                                finally {
                                                    setBusy(false);
                                                }
                                            } }), _jsxs("details", { children: [_jsx("summary", { children: "Technical evidence (original entry)" }), _jsx("pre", { children: JSON.stringify(p.value.command, null, 2) })] })] }, p.key)))] })), page === "Access Administration" && actor?.role === "admin" && (_jsx(AccessAdmin, { auth: auth })), page === "My Security" && (_jsx(SecuritySetup, { auth: auth, onDone: signOut, onSignOut: signOut })), page === "Overview" && (_jsxs(_Fragment, { children: [_jsx("p", { className: "lead", children: "From ward count to laundry intake. Every quantity has a history." }), _jsx("div", { className: "stats", children: [
                                    ["Ward counts", counts.length, "Captured sessions"],
                                    ["Loads received", receipts.length, "Laundry intake"],
                                    [
                                        "Sorting active",
                                        sorts.filter((r) => r.payload.status === "In Progress")
                                            .length,
                                        "Available to Washhouse",
                                    ],
                                    ["Customers", configs.length, "Within your access"],
                                ].map(([label, value, hint]) => (_jsxs("div", { className: "stat", children: [_jsx("span", { children: label }), _jsx("strong", { children: value }), _jsx("small", { children: hint })] }, label))) }), _jsxs("section", { className: "panel", children: [_jsxs("div", { className: "section-head", children: [_jsxs("div", { children: [_jsx("h2", { children: "Customers in factory" }), _jsx("p", { children: "Live intake and sorting progress" })] }), _jsxs("span", { className: "pill", children: [receipts.length, " loads"] })] }), receipts.length === 0 ? (_jsx(Empty, { title: "Ready for the first load", text: "Complete a ward count, then record its arrival at the laundry." })) : (_jsx("div", { className: "cards", children: receipts.map((r) => {
                                            const s = sorts.find((s) => s.payload.receiptId === r.id);
                                            return (_jsxs("article", { className: "load", children: [_jsx("span", { className: "eyebrow", children: r.payload.reference }), _jsx("h3", { children: name(r) }), _jsxs("p", { children: [r.payload.soiledBags ?? "—", " soiled bags \u00B7", " ", r.payload.vehicle || "Vehicle not entered"] }), _jsxs("div", { className: "flow", children: [_jsx("span", { className: "done", children: "Received" }), _jsx("span", { className: s ? "done" : "", children: "Sorting" }), _jsx("span", { children: s ? "Washhouse available" : "Awaiting sorting" })] }), _jsx("span", { className: "badge", children: s?.payload.status ?? "Received" })] }, r.id));
                                        }) }))] }), _jsxs("section", { className: "panel", children: [_jsx("h2", { children: "Recent ward activity" }), _jsx(RecordTable, { rows: counts.slice(0, 8), name: name, onHistory: actor?.role === "portal" ? undefined : history })] })] })), page === "Customer Setup" && (_jsx(Setup, { configs: configs, submit: submit, busy: busy })), page === "Soiled Count" && (_jsx(CountForm, { configs: configs, staff: staff, actor: actor, submit: submit, command: command, busy: busy })), page === "Soiled Linen Receipt" && (_jsx(ReceiptForm, { configs: configs, counts: counts, receipts: receipts, submit: submit, command: command, busy: busy })), page === "Soil Sorting" && (_jsx(SortForm, { configs: configs, receipts: receipts, sorts: sorts, staff: staff, submit: submit, command: command, busy: busy })), page === "Verification" && (_jsx(VerifyForm, { configs: configs, receipts: receipts, rows: rows, submit: submit, command: command, busy: busy })), page === "Attention" && (_jsx(Attention, { rows: rows, configs: configs, auth: auth, submit: submit, command: command, refresh: refresh })), page === "History" && (_jsxs("section", { className: "panel", children: [_jsx("h2", { children: "Operational record history" }), _jsx("p", { children: "Every saved revision retains its original configuration." }), _jsx(RecordTable, { rows: rows.filter((r) => r.kind !== "config"), name: name, onHistory: actor?.role === "portal" ? undefined : history }), can(["backoffice", "supervisor"]) && (_jsx("div", { className: "cards", children: rows
                                    .filter((r) => ["count", "receipt", "sorting", "verification"].includes(r.kind))
                                    .map((r) => (_jsxs("button", { onClick: () => setEditing(r), children: ["Correct ", r.kind, " \u00B7", " ", r.payload.reference ?? r.id.slice(0, 8)] }, r.id))) })), actor?.role === "portal" &&
                                rows
                                    .filter((r) => r.payload.comment ||
                                    r.payload.specialRequest ||
                                    r.kind === "verification")
                                    .map((r) => (_jsxs("article", { className: "subpanel", children: [_jsxs("h3", { children: [name(r), " \u00B7 ", r.kind] }), _jsx("p", { children: r.payload.comment }), _jsx("p", { children: r.payload.specialRequest }), r.kind === "verification" &&
                                            r.payload.lines.map((l) => (_jsxs("p", { children: [l.itemId, ": customer ", l.original, ", laundry", " ", l.quantity, ", approved estimate ", l.approvedEstimate, ", operational ", l.operationalQuantity, " \u2014 ", l.comparison] }, l.itemId)))] }, r.id)))] })), editing && (_jsx(RevisionEditor, { row: editing, rows: rows, staff: staff, configs: configs, submit: submit, command: command, close: () => setEditing(null) })), detail && (_jsx("div", { className: "modal", role: "dialog", "aria-label": "Record history", children: _jsxs("div", { children: [_jsx("button", { onClick: () => setDetail(null), children: "Close history" }), _jsx("h2", { children: "Preserved revisions and audit" }), _jsx("pre", { children: JSON.stringify(detail, null, 2) })] }) })), _jsx("footer", { children: "Coralclean Linen Management \u00B7 Phase 1 review \u00B7 Build __CORALCLEAN_VERSION__ \u00B7 Operational rules locked" })] })] }));
}
function Empty({ title, text }) {
    return (_jsxs("div", { className: "empty", children: [_jsx("div", { children: "\u25A4" }), _jsx("h3", { children: title }), _jsx("p", { children: text })] }));
}
function RecordTable({ rows, name, onHistory, }) {
    return rows.length ? (_jsx("div", { className: "table-scroll", children: _jsxs("table", { children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { children: "Customer / reference" }), _jsx("th", { children: "Activity" }), _jsx("th", { children: "Status" }), _jsx("th", { children: "Revision" }), _jsx("th", {})] }) }), _jsx("tbody", { children: rows.map((r) => (_jsxs("tr", { children: [_jsxs("td", { children: [_jsx("strong", { children: name(r) }), _jsx("small", { children: r.payload.reference ??
                                            r.payload.locationId ??
                                            r.id.slice(0, 8) })] }), _jsx("td", { children: r.kind }), _jsx("td", { children: _jsx("span", { className: "badge", children: r.payload.status ?? r.payload.basis ?? "Recorded" }) }), _jsx("td", { children: r.version }), _jsx("td", { children: onHistory && (_jsx("button", { onClick: () => onHistory(r), children: "View history" })) })] }, r.id))) })] }) })) : (_jsx(Empty, { title: "No records yet", text: "Saved activity will appear here." }));
}
function SelectContext({ configs, cid, setCid, sid, setSid, }) {
    const c = configs.find((c) => c.id === cid);
    return (_jsxs("div", { className: "form-grid", children: [_jsx(Field, { label: "Customer", children: _jsxs("select", { required: true, value: cid, onChange: (e) => {
                        setCid(e.target.value);
                        setSid("");
                    }, children: [_jsx("option", { value: "", children: "Select customer" }), configs
                            .filter((c) => c.payload.active !== false)
                            .map((c) => (_jsx("option", { value: c.id, children: c.payload.name }, c.id)))] }) }), _jsx(Field, { label: "Site", children: _jsxs("select", { required: true, value: sid, onChange: (e) => setSid(e.target.value), children: [_jsx("option", { value: "", children: "Select site" }), c?.payload.sites
                            .filter((s) => s.active !== false)
                            .map((s) => (_jsx("option", { value: s.id, children: s.name }, s.id)))] }) })] }));
}
function Assistants({ staff, values, onChange, }) {
    return (_jsxs("details", { className: "assistants", children: [_jsxs("summary", { children: ["Assistants (", values.length, ")"] }), _jsx("div", { className: "checks", children: staff.map((u) => (_jsxs("label", { children: [_jsx("input", { type: "checkbox", checked: values.includes(u.id), onChange: (e) => onChange(e.target.checked
                                ? [...values, u.id]
                                : values.filter((x) => x !== u.id)) }), u.name] }, u.id))) })] }));
}
function RetryReview({ entry, rows, actor, busy, onRetain }) {
    const [originalId, setOriginalId] = useState(""), [reason, setReason] = useState(""), [confirmed, setConfirmed] = useState(false), [error, setError] = useState("");
    const candidates = rows.filter((r) => r.kind === "count" && r.actor_id === actor?.id &&
        r.id !== entry.value.command.entityId && r.site_id === entry.value.command.siteId &&
        r.customer_id === entry.value.command.customerId && r.payload.locationId === entry.value.command.payload.locationId &&
        r.payload.status.startsWith("Completed"));
    if (!candidates.length)
        return null;
    return _jsxs("details", { className: "subpanel", children: [_jsx("summary", { children: "Was this an accidental retry of a saved count?" }), _jsx("p", { children: "Only use this when you repeated work already saved. All queued attempts for this count will be preserved as evidence, without adding linen. Genuine conflicts require supervisor review." }), _jsxs("label", { className: "field", children: ["Original saved count", _jsxs("select", { value: originalId, onChange: e => setOriginalId(e.target.value), children: [_jsx("option", { value: "", children: "Choose the original count" }), candidates.map((r) => _jsxs("option", { value: r.id, children: [new Date(r.recorded_at).toLocaleString(), " \u00B7 ", r.payload.lines.reduce((n, l) => n + sumParts(l.parts), 0), " items \u00B7 ", r.payload.bags ?? "?", " bags \u00B7 ", r.id.slice(0, 8)] }, r.id))] })] }), _jsxs("label", { className: "field", children: ["Reason", _jsx("textarea", { value: reason, onChange: e => setReason(e.target.value) })] }), _jsxs("label", { className: "check", children: [_jsx("input", { type: "checkbox", checked: confirmed, onChange: e => setConfirmed(e.target.checked) }), "I confirm these are retries, not additional linen."] }), error && _jsx("p", { role: "alert", children: error }), _jsx("button", { type: "button", disabled: busy || !confirmed || !originalId || reason.trim().length < 10, onClick: async () => { try {
                    await onRetain(originalId, reason);
                }
                catch (e) {
                    setError(e.message);
                } }, children: "Retain retry evidence \u2014 do not count again" })] });
}
function CountForm({ configs, staff, actor, submit, command, busy }) {
    const completing = useRef(false);
    const [cid, setCid] = useState(""), [sid, setSid] = useState(""), [loc, setLoc] = useState(""), [values, setValues] = useState({}), [est, setEst] = useState({}), [assist, setAssist] = useState([]), [bags, setBags] = useState(""), [yellow, setYellow] = useState(""), [unmarked, setUnmarked] = useState(false), [comment, setComment] = useState(""), [internal, setInternal] = useState(""), [special, setSpecial] = useState(""), [message, setMessage] = useState(""), [started, setStarted] = useState(now()), [ticks, setTicks] = useState([]), [draftId, setDraftId] = useState(uid()), [revision, setRevision] = useState(0), [pinned, setPinned] = useState(null);
    const c = pinned ?? configs.find((r) => r.id === cid), s = c?.payload.sites.find((s) => s.id === sid), l = s?.locations.find((l) => l.id === loc);
    const key = "draft:count:" + actor.id;
    useEffect(() => {
        get(key).then((d) => {
            if (d) {
                setCid(d.cid);
                setSid(d.sid);
                setLoc(d.loc);
                setValues(d.values);
                setEst(d.est);
                setAssist(d.assist);
                setBags(d.bags);
                setYellow(d.yellow);
                setUnmarked(d.unmarked);
                setComment(d.comment);
                setInternal(d.internal);
                setSpecial(d.special);
                setStarted(d.started);
                setTicks(d.ticks);
                setDraftId(d.draftId);
                setRevision(d.revision ?? 0);
                setPinned(d.pinned ?? null);
            }
        });
    }, []);
    async function saveDraft() {
        await put(key, {
            cid,
            sid,
            loc,
            values,
            est,
            assist,
            bags,
            yellow,
            unmarked,
            comment,
            internal,
            special,
            started,
            ticks,
            draftId,
            revision,
            pinned: c,
        });
        setMessage("Draft saved on this device");
    }
    function touch() {
        setTicks((t) => [...t, now()]);
    }
    async function complete(e, progress = false) {
        e.preventDefault();
        if (completing.current || busy)
            return;
        completing.current = true;
        setMessage("");
        try {
            const lines = l.items.map((itemId) => ({
                itemId,
                parts: values[itemId]?.trim() ? parseCalculator(values[itemId]) : [],
                estimated: est[itemId]?.trim() ? Number(est[itemId]) : null,
            }));
            if (!progress && lines.some((line) => !line.parts.length && line.estimated === null))
                throw Error("Enter a quantity or estimate for every item before completing. Enter 0 if you counted none.");
            const exceptional = unmarked || lines.some((l) => l.estimated !== null);
            const payload = {
                locationId: loc,
                status: progress
                    ? "In Progress"
                    : exceptional
                        ? "Completed with Estimate/Exception"
                        : "Completed",
                lines,
                assistants: assist,
                bags: bags === "" ? null : Number(bags),
                yellowBags: yellow === "" ? null : Number(yellow),
                unmarkedYellow: unmarked,
                comment,
                internalComment: internal,
                specialRequest: special,
                startedAt: started,
                finishedAt: progress ? null : now(),
                interactions: ticks,
            };
            const cmd = command("count", c, sid, payload);
            countSchema.parse(payload);
            if (lines.some((line) => sumParts(line.parts) > 1000000))
                throw Error("Count total exceeds the supported limit.");
            cmd.entityId = draftId;
            cmd.expectedVersion = revision;
            cmd.reason = revision ? "Count progress / completion" : "";
            const result = await submit(cmd);
            if (result && progress) {
                setRevision(result.version ?? revision + 1);
                await put(key, {
                    cid,
                    sid,
                    loc,
                    values,
                    est,
                    assist,
                    bags,
                    yellow,
                    unmarked,
                    comment,
                    internal,
                    special,
                    started,
                    ticks,
                    draftId,
                    revision: result.version ?? revision + 1,
                    pinned: c,
                });
                setMessage(result.local
                    ? "Progress queued on device"
                    : "In Progress visible to authorised users");
                return;
            }
            if (result) {
                await remove(key);
                setRevision(0);
                setValues({});
                setEst({});
                setComment("");
                setInternal("");
                setSpecial("");
                setBags("");
                setYellow("");
                setUnmarked(false);
                setDraftId(uid());
                setStarted(now());
                setTicks([]);
                setLoc("");
                setMessage(result.local ? result.message ?? "Count saved on device. Do not enter it again." :
                    "Count saved and synced successfully. Select a ward only when starting a NEW count.");
            }
        }
        catch (e) {
            setMessage(e.message);
        }
        finally {
            completing.current = false;
        }
    }
    return (_jsxs("form", { className: "panel", onSubmit: (e) => complete(e), onChange: touch, children: [_jsxs("div", { className: "section-head", children: [_jsxs("div", { children: [_jsx("h2", { children: "Hospital soiled count" }), _jsxs("p", { children: ["Counter: ", actor.name, " \u00B7 Calculator entry supports 5 + 12 + 3"] })] }), _jsx("span", { className: "pill", children: loc ? "In Progress" : "Not Started" })] }), _jsx(SelectContext, { configs: configs, cid: cid, setCid: (v) => {
                    setCid(v);
                    setPinned(configs.find((r) => r.id === v) ?? null);
                    setLoc("");
                    setValues({});
                    setEst({});
                }, sid: sid, setSid: (v) => {
                    setSid(v);
                    setLoc("");
                    setValues({});
                    setEst({});
                } }), _jsx(Field, { label: "Ward / location", children: _jsxs("select", { required: true, value: loc, onChange: (e) => {
                        setLoc(e.target.value);
                        setValues({});
                        setEst({});
                        setStarted(now());
                        setTicks([]);
                    }, children: [_jsx("option", { value: "", children: "Select location" }), s?.locations
                            .filter((l) => l.active !== false)
                            .map((l) => (_jsx("option", { value: l.id, children: l.name }, l.id)))] }) }), l && (_jsx("div", { className: "count-lines", children: l.items.map((id) => {
                    let total = "—";
                    try {
                        if (values[id])
                            total = String(sumParts(parseCalculator(values[id])));
                    }
                    catch {
                        total = "Check entry";
                    }
                    return (_jsxs("div", { className: "count-line", children: [_jsx(CountInput, { label: c.payload.items.find((i) => i.id === id)?.name ?? id, calculator: true, value: values[id] ?? "", onChange: value => { setValues({ ...values, [id]: value }); touch(); } }), _jsxs("div", { className: "total", children: [_jsx("small", { children: "Total" }), _jsx("strong", { children: total })] }), _jsx(CountInput, { label: "Estimated septic contents", value: est[id] ?? "", onChange: value => { setEst({ ...est, [id]: value }); touch(); } })] }, id));
                }) })), _jsxs("div", { className: "form-grid", children: [_jsx(CountInput, { label: "Total bags", value: bags, onChange: value => { setBags(value); touch(); } }), _jsx(CountInput, { label: "Yellow / septic bags", value: yellow, onChange: value => { setYellow(value); touch(); } })] }), _jsxs("label", { className: "check", children: [_jsx("input", { type: "checkbox", checked: unmarked, onChange: (e) => setUnmarked(e.target.checked) }), "Unmarked yellow / septic bag"] }), _jsx(Assistants, { staff: staff.filter((u) => u.id !== actor.id &&
                    (u.sites.includes("*") || u.sites.includes(sid))), values: assist, onChange: setAssist }), _jsx(Comments, { comment: comment, setComment: setComment, internal: internal, setInternal: setInternal }), _jsx(Field, { label: "Special laundry requests", children: _jsx("textarea", { value: special, onChange: (e) => setSpecial(e.target.value) }) }), message && _jsx("p", { role: "status", children: message }), _jsxs("div", { className: "actions", children: [_jsx("button", { type: "button", onClick: saveDraft, children: "Save local draft" }), _jsx("button", { type: "button", disabled: busy || !loc, onClick: (e) => complete(e, true), children: "Save progress" }), _jsx("button", { className: "primary", disabled: busy || !loc, children: "Complete count" })] })] }));
}
function Comments({ comment, setComment, internal, setInternal }) {
    return (_jsxs("div", { className: "form-grid", children: [_jsx(Field, { label: "Customer Visible \u2014 comment / protocol", children: _jsx("textarea", { value: comment, onChange: (e) => setComment(e.target.value), placeholder: "Operational information only; no patient details" }) }), _jsx(Field, { label: "Internal Only \u2014 staff note", children: _jsx("textarea", { value: internal, onChange: (e) => setInternal(e.target.value) }) })] }));
}
function Setup({ configs, submit, busy }) {
    const [selected, setSelected] = useState(""), [form, setForm] = useState(null), [reason, setReason] = useState("");
    const edit = (r) => {
        setSelected(r?.id ?? "");
        setForm(r
            ? structuredClone(r.payload)
            : {
                name: "",
                code: "",
                contact: "",
                email: "",
                phone: "",
                address: "",
                active: true,
                items: [
                    { id: uid(), name: "Sheet", description: "", active: true },
                ],
                sites: [
                    {
                        id: uid(),
                        name: "Main site",
                        model: "Linen Management",
                        policy: "Verify customer",
                        countMode: "Ward",
                        active: true,
                        locations: [],
                    },
                ],
                packing: { prepackPercent: null, instructions: "" },
                billing: {
                    basis: "",
                    periods: "",
                    cutoff: "",
                    rateDate: "Delivery date",
                    contractException: "",
                    rates: [],
                    exceptionCharges: "",
                },
                delivery: { slipRequired: false, instructions: "" },
                portal: { enabled: false, showCounter: true },
                study: { reference: "", sufficientStock: null },
            });
        setReason("");
    };
    function change(fn) {
        const next = structuredClone(form);
        fn(next);
        setForm(next);
    }
    return (_jsxs(_Fragment, { children: [_jsxs("section", { className: "panel", children: [_jsxs("div", { className: "section-head", children: [_jsx("h2", { children: "Customer directory" }), _jsx("button", { className: "primary", onClick: () => edit(), children: "+ Add customer" })] }), _jsx("div", { className: "cards", children: configs.map((r) => (_jsxs("article", { className: "load", children: [_jsxs("span", { className: "eyebrow", children: [r.payload.code, " \u00B7 VERSION ", r.version] }), _jsx("h3", { children: r.payload.name }), _jsxs("p", { children: [r.payload.sites.length, " site(s) \u00B7 ", r.payload.items.length, " ", "linen items"] }), _jsx("button", { onClick: () => edit(r), children: "Edit configuration" })] }, r.id))) })] }), form && (_jsxs("form", { className: "panel", onSubmit: async (e) => {
                    e.preventDefault();
                    const id = selected || uid();
                    const result = await submit({
                        requestId: uid(),
                        entityId: id,
                        customerId: id,
                        kind: "config",
                        expectedVersion: configs.find((r) => r.id === id)?.version ?? 0,
                        occurredAt: now(),
                        reason,
                        payload: form,
                    });
                    if (result)
                        setForm(null);
                }, children: [_jsx("h2", { children: selected ? "New configuration revision" : "New customer" }), _jsx("div", { className: "form-grid", children: [
                            "name",
                            "code",
                            "contact",
                            "email",
                            "phone",
                            "address",
                            "legalName",
                            "taxReference",
                            "postalAddress",
                        ].map((k) => (_jsx(Field, { label: k, children: _jsx("input", { required: ["name", "code"].includes(k), value: form[k] ?? "", onChange: (e) => change((v) => (v[k] = e.target.value)) }) }, k))) }), _jsxs("label", { className: "check", children: [_jsx("input", { type: "checkbox", checked: form.active, onChange: (e) => change((v) => (v.active = e.target.checked)) }), "Customer active"] }), _jsx("h3", { children: "Customer contacts" }), (form.contacts ?? []).map((contact, n) => (_jsxs("section", { className: "subpanel", children: [_jsx("div", { className: "form-grid", children: ["name", "role", "email", "phone"].map((k) => (_jsx(Field, { label: "Contact " + k, children: _jsx("input", { required: k === "name", type: k === "email" ? "email" : "text", value: contact[k] ?? "", onChange: (e) => change((v) => (v.contacts[n][k] = e.target.value)) }) }, k))) }), _jsxs("label", { className: "check", children: [_jsx("input", { type: "checkbox", checked: contact.active !== false, onChange: (e) => change((v) => (v.contacts[n].active = e.target.checked)) }), "Contact active"] }), _jsx("p", { children: "Site contacts: leave unselected for customer-wide contact." }), form.sites.map((site) => (_jsxs("label", { className: "check", children: [_jsx("input", { type: "checkbox", checked: (contact.siteIds ?? []).includes(site.id), onChange: (e) => change((v) => {
                                            const c = v.contacts[n];
                                            c.siteIds = e.target.checked
                                                ? [...(c.siteIds ?? []), site.id]
                                                : (c.siteIds ?? []).filter((s) => s !== site.id);
                                        }) }), site.name] }, site.id)))] }, contact.id))), _jsx("button", { type: "button", onClick: () => change((v) => {
                            v.contacts ??= [];
                            v.contacts.push({
                                id: uid(),
                                name: "",
                                role: "",
                                email: "",
                                phone: "",
                                siteIds: [],
                                active: true,
                            });
                        }), children: "+ Add customer contact" }), _jsx("h3", { children: "Linen items" }), form.items.map((i, n) => (_jsxs("div", { className: "form-grid", children: [_jsx(Field, { label: "Item name", children: _jsx("input", { required: true, value: i.name, onChange: (e) => change((v) => (v.items[n].name = e.target.value)) }) }), _jsx(Field, { label: "Customer description", children: _jsx("input", { value: i.description, onChange: (e) => change((v) => (v.items[n].description = e.target.value)) }) }), _jsxs("label", { className: "check", children: [_jsx("input", { type: "checkbox", checked: i.active !== false, onChange: (e) => change((v) => (v.items[n].active = e.target.checked)) }), "Active"] })] }, i.id))), _jsx("button", { type: "button", onClick: () => change((v) => v.items.push({
                            id: uid(),
                            name: "",
                            description: "",
                            active: true,
                        })), children: "+ Add linen item" }), form.sites.map((s, n) => (_jsxs("section", { className: "subpanel", children: [_jsxs("h3", { children: ["Site ", n + 1] }), _jsxs("div", { className: "form-grid", children: [_jsx(Field, { label: "Site name", children: _jsx("input", { required: true, value: s.name, onChange: (e) => change((v) => (v.sites[n].name = e.target.value)) }) }), _jsx(Field, { label: "Service model", children: _jsx("select", { value: s.model, onChange: (e) => change((v) => (v.sites[n].model = e.target.value)), children: ["Linen Management", "Wash & Return"].map((x) => (_jsx("option", { children: x }, x))) }) }), _jsx(Field, { label: "Soil-count policy", children: _jsx("select", { value: s.policy, onChange: (e) => change((v) => (v.sites[n].policy = e.target.value)), children: [
                                                "Accept customer",
                                                "Verify customer",
                                                "Full laundry count",
                                            ].map((x) => (_jsx("option", { children: x }, x))) }) }), _jsx(Field, { label: "Sorting count mode", children: _jsxs("select", { value: s.countMode, onChange: (e) => change((v) => (v.sites[n].countMode = e.target.value)), children: [_jsx("option", { children: "Ward" }), _jsx("option", { children: "Bulk" })] }) })] }), _jsxs("label", { className: "check", children: [_jsx("input", { type: "checkbox", checked: s.active !== false, onChange: (e) => change((v) => (v.sites[n].active = e.target.checked)) }), "Site active"] }), s.locations.map((l, j) => (_jsxs("div", { className: "location", children: [_jsxs("div", { className: "form-grid", children: [_jsx(Field, { label: "Location name", children: _jsx("input", { required: true, value: l.name, onChange: (e) => change((v) => (v.sites[n].locations[j].name = e.target.value)) }) }), _jsx(Field, { label: "Location type", children: _jsx("select", { value: l.kind, onChange: (e) => change((v) => {
                                                        const l = v.sites[n].locations[j];
                                                        l.kind = e.target.value;
                                                        if (l.kind !== "Ward") {
                                                            l.beds = null;
                                                            l.occupancy = null;
                                                        }
                                                    }), children: ["Ward", "Central holding", "Other"].map((x) => (_jsx("option", { children: x }, x))) }) }), l.kind === "Ward" && (_jsxs(_Fragment, { children: [_jsx(Field, { label: "Ward type", children: _jsx("input", { value: l.wardType ?? "", onChange: (e) => change((v) => (v.sites[n].locations[j].wardType =
                                                                e.target.value)) }) }), _jsx(Field, { label: "Beds", children: _jsx("input", { type: "number", min: "0", value: l.beds ?? "", onChange: (e) => change((v) => (v.sites[n].locations[j].beds =
                                                                e.target.value === ""
                                                                    ? null
                                                                    : Number(e.target.value))) }) }), _jsx(Field, { label: "Average occupancy (%)", children: _jsx("input", { type: "number", min: "0", max: "100", value: l.occupancy ?? "", onChange: (e) => change((v) => (v.sites[n].locations[j].occupancy =
                                                                e.target.value === ""
                                                                    ? null
                                                                    : Number(e.target.value))) }) })] }))] }), _jsxs("label", { className: "check", children: [_jsx("input", { type: "checkbox", checked: l.active !== false, onChange: (e) => change((v) => (v.sites[n].locations[j].active = e.target.checked)) }), "Location active"] }), _jsx("p", { children: "Applicable items and stock settings" }), form.items.map((i) => (_jsxs("div", { className: "stock-line", children: [_jsxs("label", { className: "check", children: [_jsx("input", { type: "checkbox", checked: l.items.includes(i.id), onChange: (e) => change((v) => {
                                                            const q = v.sites[n].locations[j];
                                                            q.items = e.target.checked
                                                                ? [...q.items, i.id]
                                                                : q.items.filter((id) => id !== i.id);
                                                        }) }), i.name] }), ["allocations", "targets", "safety"].map((k) => (_jsx(Field, { label: k, children: _jsx("input", { type: "number", min: "0", value: l[k]?.[i.id] ?? "", onChange: (e) => change((v) => {
                                                        const q = v.sites[n].locations[j];
                                                        q[k] ??= {};
                                                        if (e.target.value === "")
                                                            delete q[k][i.id];
                                                        else
                                                            q[k][i.id] = Number(e.target.value);
                                                    }) }) }, k)))] }, i.id)))] }, l.id))), _jsx("button", { type: "button", onClick: () => change((v) => v.sites[n].locations.push({
                                    id: uid(),
                                    name: "",
                                    kind: "Ward",
                                    beds: null,
                                    occupancy: null,
                                    items: [],
                                    allocations: {},
                                    targets: {},
                                    safety: {},
                                    active: true,
                                })), children: "+ Add location" })] }, s.id))), _jsx("button", { type: "button", onClick: () => change((v) => v.sites.push({
                            id: uid(),
                            name: "",
                            model: "Linen Management",
                            policy: "Verify customer",
                            countMode: "Ward",
                            locations: [],
                            active: true,
                        })), children: "+ Add site" }), _jsxs("details", { className: "subpanel", children: [_jsx("summary", { children: "Packing, billing, portal and study settings" }), _jsxs("div", { className: "form-grid", children: [_jsx(Field, { label: "Pre-pack percentage", children: _jsx("input", { type: "number", min: "0", max: "100", value: form.packing.prepackPercent ?? "", onChange: (e) => change((v) => (v.packing.prepackPercent =
                                                e.target.value === ""
                                                    ? null
                                                    : Number(e.target.value))) }) }), _jsx(Field, { label: "Packing instructions", children: _jsx("textarea", { value: form.packing.instructions, onChange: (e) => change((v) => (v.packing.instructions = e.target.value)) }) }), [
                                        "basis",
                                        "periods",
                                        "cutoff",
                                        "contractException",
                                        "exceptionCharges",
                                    ].map((k) => (_jsx(Field, { label: "Billing " + k, children: _jsx("input", { value: form.billing[k], onChange: (e) => change((v) => (v.billing[k] = e.target.value)) }) }, k))), _jsx(Field, { label: "Rate date", children: _jsxs("select", { value: form.billing.rateDate, onChange: (e) => change((v) => (v.billing.rateDate = e.target.value)), children: [_jsx("option", { children: "Delivery date" }), _jsx("option", { children: "Contract exception" })] }) }), _jsx(Field, { label: "Delivery instructions", children: _jsx("textarea", { value: form.delivery.instructions, onChange: (e) => change((v) => (v.delivery.instructions = e.target.value)) }) }), _jsx(Field, { label: "Seven-day study reference", children: _jsx("input", { value: form.study.reference, onChange: (e) => change((v) => (v.study.reference = e.target.value)) }) }), _jsx(Field, { label: "Study had sufficient stock", children: _jsxs("select", { value: String(form.study.sufficientStock), onChange: (e) => change((v) => (v.study.sufficientStock =
                                                e.target.value === "null"
                                                    ? null
                                                    : e.target.value === "true")), children: [_jsx("option", { value: "null", children: "Not assessed" }), _jsx("option", { value: "true", children: "Yes" }), _jsx("option", { value: "false", children: "No" })] }) })] }), [
                                ["delivery", "slipRequired", "Customer slip required"],
                                ["portal", "enabled", "Enable customer portal"],
                                ["portal", "showCounter", "Show named counters"],
                            ].map(([g, k, l]) => (_jsxs("label", { className: "check", children: [_jsx("input", { type: "checkbox", checked: form[g][k], onChange: (e) => change((v) => (v[g][k] = e.target.checked)) }), l] }, k))), _jsx("h3", { children: "Effective-dated rates" }), form.billing.rates.map((r, n) => (_jsxs("div", { className: "form-grid", children: [_jsx(Field, { label: "Rate item", children: _jsx("select", { value: r.itemId, onChange: (e) => change((v) => (v.billing.rates[n].itemId = e.target.value)), children: form.items.map((i) => (_jsx("option", { value: i.id, children: i.name }, i.id))) }) }), _jsx(Field, { label: "Rate", children: _jsx("input", { type: "number", step: "0.01", min: "0", value: r.rate, onChange: (e) => change((v) => (v.billing.rates[n].rate = Number(e.target.value))) }) }), _jsx(Field, { label: "Effective from", children: _jsx("input", { required: true, type: "date", value: r.effectiveFrom, onChange: (e) => change((v) => (v.billing.rates[n].effectiveFrom = e.target.value)) }) }), _jsx(Field, { label: "Currency", children: _jsx("input", { value: r.currency, onChange: (e) => change((v) => (v.billing.rates[n].currency = e.target.value)) }) })] }, n))), _jsx("button", { type: "button", onClick: () => change((v) => v.billing.rates.push({
                                    itemId: v.items[0].id,
                                    rate: 0,
                                    effectiveFrom: new Date().toISOString().slice(0, 10),
                                    currency: "ZAR",
                                })), children: "+ Add rate" })] }), selected && (_jsx(Field, { label: "Reason for configuration change", children: _jsx("input", { required: true, value: reason, onChange: (e) => setReason(e.target.value) }) })), _jsxs("div", { className: "actions", children: [_jsx("button", { type: "button", onClick: () => setForm(null), children: "Cancel" }), _jsx("button", { className: "primary", disabled: busy, children: "Save configuration revision" })] })] }))] }));
}
function ReceiptForm({ configs, counts, receipts, submit, command, busy, }) {
    const [cid, setCid] = useState(""), [sid, setSid] = useState(""), [selected, setSelected] = useState({}), [fields, setFields] = useState({}), [comment, setComment] = useState(""), [internal, setInternal] = useState("");
    const c = configs.find((c) => c.id === cid);
    const relevant = counts.filter((r) => r.customer_id === cid &&
        r.site_id === sid &&
        r.payload.status.startsWith("Completed"));
    function available(count, line) {
        const alloc = receipts
            .flatMap((r) => r.payload.allocations)
            .filter((a) => a.countId === count.id && a.itemId === line.itemId);
        return {
            q: Math.max(0, sumParts(line.parts) -
                alloc.reduce((a, b) => a + b.quantity, 0)),
            e: Math.max(0, (line.estimated ?? 0) -
                alloc.reduce((a, b) => a + b.estimatedQuantity, 0)),
        };
    }
    return (_jsxs("form", { className: "panel", onSubmit: async (e) => {
            e.preventDefault();
            const allocations = relevant.flatMap((r) => r.payload.lines
                .filter((l) => selected[r.id + ":" + l.itemId])
                .map((l) => ({
                countId: r.id,
                countVersion: r.version,
                itemId: l.itemId,
                quantity: Number(selected[r.id + ":" + l.itemId].quantity),
                estimatedQuantity: Number(selected[r.id + ":" + l.itemId].estimate),
            })));
            const payload = {
                allocations,
                comment,
                internalComment: internal,
            };
            for (const k of ["slip", "driver", "assistant", "vehicle"])
                payload[k] = fields[k] ?? "";
            for (const k of ["soiledBags", "km"])
                payload[k] = fields[k] ? Number(fields[k]) : null;
            for (const k of ["arrival", "departure"])
                payload[k] = fields[k] ? new Date(fields[k]).toISOString() : null;
            const r = await submit(command("receipt", c, sid, payload));
            if (r) {
                setFields({});
                setSelected({});
                setComment("");
                setInternal("");
            }
        }, children: [_jsx("h2", { children: "Receive soiled linen" }), _jsx("p", { children: "Link all or part of completed ward counts. Missing optional fields remain visible for cleanup." }), _jsx(SelectContext, { configs: configs, cid: cid, setCid: (v) => {
                    setCid(v);
                    setSelected({});
                }, sid: sid, setSid: (v) => {
                    setSid(v);
                    setSelected({});
                } }), c && (_jsx("p", { className: "badge", children: c.payload.sites.find((s) => s.id === sid)?.policy ??
                    "Choose site" })), _jsx("h3", { children: "Available count quantities" }), relevant.length === 0 ? (_jsx("p", { children: "No completed counts for this site. Laundry-side counting may still follow receipt." })) : (relevant.map((r) => (_jsxs("div", { className: "subpanel", children: [_jsxs("strong", { children: [r.payload.locationId, " \u00B7 count revision ", r.version] }), r.payload.lines.map((l) => {
                        const key = r.id + ":" + l.itemId, a = available(r, l);
                        return (_jsxs("div", { className: "stock-line", children: [_jsxs("label", { className: "check", children: [_jsx("input", { type: "checkbox", checked: !!selected[key], onChange: (e) => {
                                                const v = { ...selected };
                                                if (e.target.checked)
                                                    v[key] = {
                                                        quantity: String(a.q),
                                                        estimate: String(a.e),
                                                    };
                                                else
                                                    delete v[key];
                                                setSelected(v);
                                            } }), c.payload.items.find((i) => i.id === l.itemId)?.name, " ", "(", a.q, " + ", a.e, " estimated available)"] }), selected[key] && (_jsxs(_Fragment, { children: [_jsx(Field, { label: "Allocate actual", children: _jsx("input", { type: "number", min: "0", max: a.q, value: selected[key].quantity, onChange: (e) => setSelected({
                                                    ...selected,
                                                    [key]: {
                                                        ...selected[key],
                                                        quantity: e.target.value,
                                                    },
                                                }) }) }), _jsx(Field, { label: "Allocate estimated", children: _jsx("input", { type: "number", min: "0", max: a.e, value: selected[key].estimate, onChange: (e) => setSelected({
                                                    ...selected,
                                                    [key]: {
                                                        ...selected[key],
                                                        estimate: e.target.value,
                                                    },
                                                }) }) })] }))] }, key));
                    })] }, r.id)))), _jsx("div", { className: "form-grid", children: [
                    ["slip", "Customer soil/delivery slip"],
                    ["soiledBags", "Soiled bags collected"],
                    ["driver", "Driver"],
                    ["assistant", "Van assistant"],
                    ["vehicle", "Vehicle"],
                    ["arrival", "Arrival time"],
                    ["departure", "Departure time"],
                    ["km", "KM reading"],
                ].map(([k, label]) => (_jsx(Field, { label: label, children: _jsx("input", { type: ["arrival", "departure"].includes(k)
                            ? "datetime-local"
                            : ["soiledBags", "km"].includes(k)
                                ? "number"
                                : "text", min: "0", value: fields[k] ?? "", onChange: (e) => setFields({ ...fields, [k]: e.target.value }) }) }, k))) }), _jsx(Comments, { comment: comment, setComment: setComment, internal: internal, setInternal: setInternal }), _jsx("div", { className: "actions", children: _jsx("button", { className: "primary", disabled: busy || !sid, children: "Record soiled linen receipt" }) })] }));
}
function SortForm({ configs, receipts, sorts, staff, submit, command, busy, }) {
    const [rid, setRid] = useState(""), [assist, setAssist] = useState([]), [bags, setBags] = useState(""), [comment, setComment] = useState(""), [internal, setInternal] = useState(""), [values, setValues] = useState({}), [interactionTicks, setInteractionTicks] = useState([]), [query, setQuery] = useState(""), [statusFilter, setStatusFilter] = useState("All"), [dateFilter, setDateFilter] = useState("");
    const filtered = receipts.filter((r) => {
        const label = [
            configs.find((c) => c.id === r.customer_id)?.payload.name,
            r.site_id,
            r.payload.reference,
        ]
            .join(" ")
            .toLowerCase();
        const state = sorts.find((s) => s.payload.receiptId === r.id)?.payload.status ??
            "Not Started";
        return (label.includes(query.toLowerCase()) &&
            (statusFilter === "All" || state === statusFilter) &&
            (!dateFilter || r.recorded_at.slice(0, 10) === dateFilter));
    });
    const receipt = receipts.find((r) => r.id === rid), base = configs.find((c) => c.id === receipt?.customer_id), c = base && { ...base, payload: receipt?.configuration ?? base.payload }, site = c?.payload.sites.find((s) => s.id === receipt?.site_id), old = sorts.find((s) => s.payload.receiptId === rid);
    const lines = site
        ? site.countMode === "Ward"
            ? site.locations.flatMap((l) => l.items.map((itemId) => ({ itemId, locationId: l.id })))
            : c.payload.items.map((i) => ({ itemId: i.id }))
        : [];
    async function save(complete) {
        const payload = {
            receiptId: rid,
            status: complete ? "Completed" : "In Progress",
            assistants: assist,
            bags: bags === "" ? null : Number(bags),
            comment,
            internalComment: internal,
            startedAt: old?.payload.startedAt ?? now(),
            finishedAt: complete ? now() : null,
            interactions: [
                ...(old?.payload.interactions ?? []),
                ...interactionTicks,
                now(),
            ],
            lines: lines.map((l) => ({
                ...l,
                quantity: values[(l.locationId ?? "bulk") + ":" + l.itemId]
                    ? Number(values[(l.locationId ?? "bulk") + ":" + l.itemId])
                    : null,
            })),
        };
        const cmd = command("sorting", c, receipt.site_id, payload, old, old ? "Sorting progress recorded" : "");
        cmd.config_id = undefined;
        cmd.configId = receipt.config_id;
        cmd.configVersion = receipt.config_version;
        delete cmd.config_id;
        await submit(cmd);
    }
    return (_jsxs("section", { className: "panel", onChange: () => setInteractionTicks((t) => [...t, now()]), children: [_jsx("h2", { children: "Soil sorting" }), _jsx("p", { children: "Starting sorting makes work available to Washhouse. Optional missing fields do not stop completion." }), _jsxs("div", { className: "form-grid", children: [_jsx(Field, { label: "Filter customer, site or load", children: _jsx("input", { value: query, onChange: (e) => setQuery(e.target.value) }) }), _jsx(Field, { label: "Sorting status", children: _jsx("select", { value: statusFilter, onChange: (e) => setStatusFilter(e.target.value), children: [
                                "All",
                                "Not Started",
                                "In Progress",
                                "Completed",
                                "Completed with Estimate/Exception",
                            ].map((s) => (_jsx("option", { children: s }, s))) }) }), _jsx(Field, { label: "Receipt date (UTC)", children: _jsx("input", { type: "date", value: dateFilter, onChange: (e) => setDateFilter(e.target.value) }) })] }), _jsx(Field, { label: "Received load", children: _jsxs("select", { value: rid, onChange: (e) => {
                        setRid(e.target.value);
                        setInteractionTicks([]);
                        const previous = sorts.find((s) => s.payload.receiptId === e.target.value);
                        setComment(previous?.payload.comment ?? "");
                        setInternal(previous?.payload.internalComment ?? "");
                        setBags(String(previous?.payload.bags ?? ""));
                        setValues(Object.fromEntries((previous?.payload.lines ?? []).map((l) => [
                            (l.locationId ?? "bulk") + ":" + l.itemId,
                            String(l.quantity ?? ""),
                        ])));
                        setAssist(previous?.payload.assistants ?? []);
                    }, children: [_jsx("option", { value: "", children: "Select received load" }), filtered.map((r) => (_jsxs("option", { value: r.id, children: [r.payload.reference, " \u00B7", " ", configs.find((c) => c.id === r.customer_id)?.payload.name] }, r.id)))] }) }), receipt && (_jsxs(_Fragment, { children: [_jsxs("p", { className: "badge", children: [site?.countMode, " count \u00B7 ", site?.policy, " \u00B7", " ", old?.payload.status ?? "Not Started"] }), _jsx(Assistants, { staff: staff.filter((u) => u.sites.includes("*") || u.sites.includes(receipt.site_id)), values: assist, onChange: setAssist }), _jsx(Field, { label: "Bags handled", children: _jsx("input", { type: "number", min: "0", value: bags, onChange: (e) => setBags(e.target.value) }) }), site?.policy !== "Accept customer" && (_jsx("div", { className: "form-grid", children: lines.map((l) => {
                            const key = (l.locationId ?? "bulk") + ":" + l.itemId;
                            return (_jsx(Field, { label: (l.locationId
                                    ? site.locations.find((w) => w.id === l.locationId)
                                        ?.name + " · "
                                    : "") +
                                    c.payload.items.find((i) => i.id === l.itemId)?.name, children: _jsx("input", { type: "number", min: "0", value: values[key] ?? "", onChange: (e) => setValues({ ...values, [key]: e.target.value }) }) }, key));
                        }) })), _jsx(Comments, { comment: comment, setComment: setComment, internal: internal, setInternal: setInternal }), _jsxs("div", { className: "actions", children: [_jsx("button", { disabled: busy, onClick: () => save(false), children: old ? "Save progress" : "Start sorting" }), _jsx("button", { className: "primary", disabled: busy, onClick: () => save(true), children: "Complete sorting" })] })] }))] }));
}
function VerifyForm({ configs, receipts, rows, submit, command, busy }) {
    const [rid, setRid] = useState(""), [values, setValues] = useState({}), [est, setEst] = useState({}), [included, setIncluded] = useState({}), [reason, setReason] = useState("");
    const r = receipts.find((r) => r.id === rid), base = configs.find((c) => c.id === r?.customer_id), c = base && { ...base, payload: r?.configuration ?? base.payload }, site = c?.payload.sites.find((s) => s.id === r?.site_id), basis = site
        ? {
            "Accept customer": "Accepted customer count",
            "Verify customer": "Verified laundry count",
            "Full laundry count": "Full laundry count",
        }[site.policy]
        : "", old = rows.find((v) => v.kind === "verification" && v.payload.receiptId === rid);
    const options = site
        ? site.countMode === "Ward"
            ? site.locations.flatMap((l) => l.items.map((itemId) => ({
                itemId,
                locationId: l.id,
                name: l.name +
                    " · " +
                    c.payload.items.find((i) => i.id === itemId)?.name,
                key: l.id + ":" + itemId,
            })))
            : c.payload.items.map((i) => ({
                itemId: i.id,
                name: i.name,
                key: "bulk:" + i.id,
            }))
        : [];
    return (_jsxs("form", { className: "panel", onSubmit: async (e) => {
            e.preventDefault();
            const lines = options
                .filter((i) => values[i.key] !== undefined && values[i.key] !== "")
                .map((i) => ({
                itemId: i.itemId,
                ...(i.locationId ? { locationId: i.locationId } : {}),
                quantity: Number(values[i.key]),
                approvedEstimate: Number(est[i.key] || 0),
                includesSeptic: included[i.key] ?? false,
            }));
            const cmd = command("verification", c, r.site_id, { receiptId: rid, basis, lines, reason }, old, reason);
            cmd.configId = r.config_id;
            cmd.configVersion = r.config_version;
            await submit(cmd);
        }, children: [_jsx("h2", { children: "Approve operational quantities" }), _jsx("p", { children: "Original ward counts and estimates are preserved. Blank rows stay unverified; zero is an explicit quantity." }), _jsx(Field, { label: "Received load", children: _jsxs("select", { required: true, value: rid, onChange: (e) => {
                        setRid(e.target.value);
                        const prior = rows.find((v) => v.kind === "verification" &&
                            v.payload.receiptId === e.target.value);
                        const values = {}, estimates = {}, flags = {};
                        for (const l of prior?.payload.lines ?? []) {
                            const key = (l.locationId ?? "bulk") + ":" + l.itemId;
                            values[key] = String(l.quantity);
                            estimates[key] = String(l.approvedEstimate);
                            flags[key] = l.includesSeptic;
                        }
                        setValues(values);
                        setEst(estimates);
                        setIncluded(flags);
                        setReason("");
                    }, children: [_jsx("option", { value: "", children: "Select load" }), receipts.map((r) => (_jsx("option", { value: r.id, children: r.payload.reference }, r.id)))] }) }), r && (_jsxs(_Fragment, { children: [_jsxs("p", { className: "badge", children: [basis, " \u00B7 ", site.countMode] }), old && (_jsxs("p", { children: ["Saved approval:", " ", old.payload.coverage ??
                                "Earlier item-level approval; review ward attribution", " ", "\u00B7 revision ", old.version] })), options.map((i) => {
                        const inputs = r.payload.allocations.filter((a) => a.itemId === i.itemId &&
                            (!i.locationId || a.sourceLocationId === i.locationId)), original = inputs.reduce((n, a) => n + a.quantity, 0), estimate = inputs.reduce((n, a) => n + a.estimatedQuantity, 0), v = values[i.key], comparisonBase = original + (included[i.key] ? estimate : 0);
                        const status = v === undefined || v === ""
                            ? "Awaiting verification"
                            : basis === "Accepted customer count"
                                ? "Accepted customer count"
                                : !inputs.length
                                    ? "No customer baseline"
                                    : Number(v) === comparisonBase
                                        ? "Checked and correct"
                                        : Number(v) > comparisonBase
                                            ? "Laundry count higher"
                                            : "Laundry count lower";
                        return (_jsxs("details", { className: "subpanel", open: inputs.length > 0 || values[i.key] !== undefined, children: [_jsxs("summary", { children: [i.name, " \u00B7 ", status] }), _jsxs("p", { children: ["Customer actual: ", original, " \u00B7 Estimated septic: ", estimate] }), _jsx("span", { className: "badge " +
                                        (status === "Laundry count higher"
                                            ? "blue"
                                            : status === "Laundry count lower"
                                                ? "red"
                                                : status === "Checked and correct"
                                                    ? "black"
                                                    : ""), children: status }), _jsxs("div", { className: "form-grid", children: [_jsx(Field, { label: "Approved quantity " + i.name, children: _jsx("input", { type: "number", min: "0", value: v ?? "", onChange: (e) => setValues({ ...values, [i.key]: e.target.value }) }) }), _jsx(Field, { label: "Approved septic estimate " + i.name, children: _jsx("input", { type: "number", min: "0", max: estimate, disabled: included[i.key], value: est[i.key] ?? "", onChange: (e) => setEst({ ...est, [i.key]: e.target.value }) }) })] }), _jsxs("label", { className: "check", children: [_jsx("input", { type: "checkbox", checked: included[i.key] ?? false, onChange: (e) => {
                                                setIncluded({ ...included, [i.key]: e.target.checked });
                                                if (e.target.checked)
                                                    setEst({ ...est, [i.key]: "0" });
                                            } }), "Verified quantity already includes septic contents"] })] }, i.key));
                    }), _jsx(Field, { label: "Approval / reconciliation reason", children: _jsx("input", { required: true, value: reason, onChange: (e) => setReason(e.target.value) }) }), _jsx("div", { className: "actions", children: _jsx("button", { className: "primary", disabled: busy, children: "Approve count basis" }) })] }))] }));
}
function Attention({ rows, configs, auth, submit, command, refresh }) {
    const [conflicts, setConflicts] = useState([]), [message, setMessage] = useState(""), [target, setTarget] = useState(""), [field, setField] = useState("km"), [value, setValue] = useState(""), [classification, setClassification] = useState("Estimated"), [reason, setReason] = useState(""), [vdesc, setVdesc] = useState(""), [day, setDay] = useState("");
    useEffect(() => {
        api("/conflicts")
            .then(setConflicts)
            .catch((e) => setMessage(e.message));
    }, [rows]);
    const records = rows.filter((r) => ["count", "receipt", "sorting"].includes(r.kind) &&
        (!day ||
            new Date(r.occurred_at ?? r.recorded_at).toLocaleDateString("en-CA") ===
                day));
    const expectedFields = (r) => r.kind === "receipt"
        ? [
            "arrival",
            "departure",
            "km",
            "driver",
            "assistant",
            "vehicle",
            "soiledBags",
            ...(r.configuration?.delivery?.slipRequired ? ["slip"] : []),
        ]
        : r.kind === "sorting"
            ? ["finishedAt", "bags"]
            : ["bags"];
    const expectedTotal = records.reduce((n, r) => n + expectedFields(r).length, 0);
    const missing = records.flatMap((r) => expectedFields(r)
        .filter((k) => r.payload[k] === null || r.payload[k] === "")
        .map((k) => ({
        r,
        field: k,
        filled: rows.some((c) => c.kind === "cleanup" &&
            c.payload.targetId === r.id &&
            c.payload.field === k),
    })));
    const open = missing.filter((m) => !m.filled);
    const actual = missing.filter((m) => m.filled &&
        rows.some((r) => r.kind === "cleanup" &&
            r.payload.targetId === m.r.id &&
            r.payload.field === m.field &&
            r.payload.classification === "Actual")).length;
    const estimated = missing.filter((m) => m.filled).length - actual;
    return (_jsxs(_Fragment, { children: [_jsxs("section", { className: "panel", children: [_jsx("h2", { children: "Data completeness" }), _jsx(Field, { label: "Activity date (local time; blank shows all dates)", children: _jsx("input", { type: "date", value: day, onChange: (e) => setDay(e.target.value) }) }), _jsxs("p", { children: [open.length, " missing fields \u00B7 ", actual, " supplied actual \u00B7 ", estimated, " ", "supplied estimated"] }), _jsxs("p", { children: [expectedTotal
                                ? Math.round(((expectedTotal - open.length) / expectedTotal) * 100)
                                : 0, "% supporting fields supplied \u00B7", " ", expectedTotal - missing.length + actual, " actual \u00B7 ", estimated, " ", "estimated \u00B7 ", expectedTotal, " applicable fields. Estimates are not counted as actual observations."] }), _jsx("div", { className: "table-scroll", children: _jsxs("table", { children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { children: "Reference" }), _jsx("th", { children: "Field" }), _jsx("th", { children: "State" })] }) }), _jsx("tbody", { children: missing.map((m) => (_jsxs("tr", { children: [_jsx("td", { children: m.r.payload.reference ??
                                                    m.r.kind + " " + m.r.id.slice(0, 8) }), _jsx("td", { children: m.field }), _jsx("td", { children: m.filled ? "Supporting entry recorded" : "Missing" })] }, m.r.id + m.field))) })] }) })] }), _jsxs("form", { className: "panel", onSubmit: async (e) => {
                    e.preventDefault();
                    const t = records.find((r) => r.id === target), c = configs.find((c) => c.id === t.customer_id);
                    await submit(command("cleanup", c, t.site_id, {
                        targetId: target,
                        field,
                        value: field === "km" ? Number(value) : new Date(value).toISOString(),
                        classification,
                        reason,
                    }));
                }, children: [_jsx("h2", { children: "End-of-day supporting information" }), _jsx(Field, { label: "Cleanup target", children: _jsxs("select", { required: true, value: target, onChange: (e) => setTarget(e.target.value), children: [_jsx("option", { value: "", children: "Select record" }), records.map((r) => (_jsxs("option", { value: r.id, children: [r.kind, " \u00B7 ", r.payload.reference ?? r.id.slice(0, 8)] }, r.id)))] }) }), _jsxs("div", { className: "form-grid", children: [_jsx(Field, { label: "Missing field", children: _jsx("select", { value: field, onChange: (e) => {
                                        setField(e.target.value);
                                        setValue("");
                                    }, children: ["km", "arrival", "departure", "finishedAt"].map((f) => (_jsx("option", { children: f }, f))) }) }), _jsx(Field, { label: "Value", children: _jsx("input", { required: true, type: field === "km" ? "number" : "datetime-local", min: "0", value: value, onChange: (e) => setValue(e.target.value) }) }), _jsx(Field, { label: "Classification", children: _jsxs("select", { value: classification, onChange: (e) => setClassification(e.target.value), children: [_jsx("option", { children: "Estimated" }), _jsx("option", { children: "Actual" })] }) }), _jsx(Field, { label: "Supporting reason", children: _jsx("input", { required: true, value: reason, onChange: (e) => setReason(e.target.value) }) })] }), _jsx("button", { className: "primary", disabled: !target, children: "Save supporting entry" })] }), _jsxs("section", { className: "panel", children: [_jsx("h2", { children: "Operational variances" }), _jsx("p", { children: "Open variances do not stop operational flow." }), _jsx(Field, { label: "Variance description (uses selected target above)", children: _jsx("input", { value: vdesc, onChange: (e) => setVdesc(e.target.value) }) }), _jsx("button", { disabled: !target || !vdesc, onClick: async () => {
                            const t = records.find((r) => r.id === target), c = configs.find((c) => c.id === t.customer_id);
                            await submit(command("variance", c, t.site_id, {
                                targetId: target,
                                type: "Protocol",
                                description: vdesc,
                                status: "Open",
                                resolution: "",
                                visibility: "Internal Only",
                            }));
                            setVdesc("");
                        }, children: "Flag variance" }), rows
                        .filter((r) => r.kind === "variance")
                        .map((r) => (_jsxs("div", { className: "subpanel", children: [_jsx("strong", { children: r.payload.description }), _jsx("p", { children: r.payload.status }), r.payload.status === "Open" && (_jsx("button", { onClick: async () => {
                                    const text = prompt("Resolution reason");
                                    if (text) {
                                        const c = configs.find((c) => c.id === r.customer_id);
                                        await submit(command("variance", c, r.site_id, {
                                            ...r.payload,
                                            status: "Resolved",
                                            resolution: text,
                                        }, r, text));
                                    }
                                }, children: "Resolve with reason" }))] }, r.id)))] }), _jsxs("section", { className: "panel", children: [_jsx("h2", { children: "Preserved synchronisation conflicts" }), conflicts.length === 0 ? (_jsx("p", { children: "No unresolved conflicts." })) : (conflicts.map((c) => (_jsxs("div", { className: "subpanel", children: [_jsxs("strong", { children: [c.command.kind, " \u00B7 server revision ", c.current_version] }), _jsx(PayloadSummary, { payload: c.command.payload }), _jsx("button", { onClick: async () => {
                                    const reason = prompt("Reason for adopting these entries as a new controlled revision");
                                    if (!reason)
                                        return;
                                    const current = rows.find((r) => r.id === c.entity_id);
                                    if (!current) {
                                        setMessage("Refresh to load the current record");
                                        return;
                                    }
                                    const result = await submit({
                                        ...c.command,
                                        requestId: uid(),
                                        expectedVersion: current.version,
                                        resolutionOf: c.id,
                                        reason,
                                        occurredAt: now(),
                                    });
                                    if (result)
                                        await refresh();
                                }, children: "Adopt submitted entries, with reason" }), _jsx("button", { onClick: async () => {
                                    const reason = prompt("Reason for keeping the current server revision");
                                    if (!reason)
                                        return;
                                    try {
                                        const r = rows.find((r) => r.id === c.entity_id);
                                        await api("/conflicts/" + c.id + "/resolve", { reason, acceptedRevision: r.version }, auth.csrf);
                                        await refresh();
                                    }
                                    catch (e) {
                                        setMessage(e.message);
                                    }
                                }, children: "Keep current revision, with reason" }), _jsx("p", { children: "For a combined correction, use History to create the reviewed revision, then keep that revision here. Submitted evidence is always preserved." })] }, c.id)))), message && _jsx("p", { role: "alert", children: message })] })] }));
}
function PayloadSummary({ payload }) {
    return (_jsxs("div", { children: [Object.entries(payload)
                .filter(([k, v]) => !["interactions", "internalComment"].includes(k) &&
                v !== null &&
                ["string", "number", "boolean"].includes(typeof v))
                .map(([k, v]) => (_jsxs("p", { children: [_jsxs("strong", { children: [k.replace(/([A-Z])/g, " $1"), ": "] }), String(v)] }, k))), payload.internalComment && (_jsxs("p", { children: [_jsx("strong", { children: "Internal Only: " }), payload.internalComment] })), payload.lines && (_jsxs("table", { children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { children: "Ward/item" }), _jsx("th", { children: "Entered quantity" }), _jsx("th", { children: "Estimate" })] }) }), _jsx("tbody", { children: payload.lines.map((l, n) => (_jsxs("tr", { children: [_jsxs("td", { children: [l.locationId ?? payload.locationId ?? "Bulk", " \u00B7 ", l.itemId] }), _jsx("td", { children: l.parts?.join(" + ") ?? l.quantity ?? "Missing" }), _jsx("td", { children: l.estimated ?? l.approvedEstimate ?? "—" })] }, n))) })] }))] }));
}
function RevisionEditor({ row, rows, staff, configs, submit, command, close, }) {
    const [payload, setPayload] = useState(() => editablePayload(row.kind, row.payload)), [reason, setReason] = useState(""), [parts, setParts] = useState(() => Object.fromEntries((row.payload.lines ?? [])
        .filter((l) => l.parts)
        .map((l) => [l.itemId, l.parts.join(" + ")]))), [error, setError] = useState("");
    function field(k, v) {
        setPayload({ ...payload, [k]: v });
    }
    const localTime = (value) => value
        ? new Date(new Date(value).getTime() -
            new Date(value).getTimezoneOffset() * 60000)
            .toISOString()
            .slice(0, 16)
        : "";
    return (_jsx("div", { className: "modal", role: "dialog", "aria-label": "Audited correction", children: _jsxs("form", { onSubmit: async (e) => {
                e.preventDefault();
                try {
                    const p = structuredClone(payload);
                    if (row.kind === "count")
                        for (const l of p.lines)
                            l.parts = parts[l.itemId]?.trim()
                                ? parseCalculator(parts[l.itemId])
                                : [];
                    const c = configs.find((c) => c.id === row.customer_id);
                    const result = await submit(command(row.kind, c, row.site_id, p, row, reason));
                    if (result)
                        close();
                }
                catch (e) {
                    setError(e.message);
                }
            }, children: [_jsxs("h2", { children: ["Correct ", row.kind, " \u00B7 revision ", row.version] }), _jsx("p", { children: "The original revision remains unchanged. Your correction creates a new revision." }), _jsxs("div", { className: "form-grid", children: [Object.entries(payload)
                            .filter(([k, v]) => [
                            "slip",
                            "driver",
                            "assistant",
                            "vehicle",
                            "comment",
                            "internalComment",
                            "specialRequest",
                        ].includes(k) && typeof v === "string")
                            .map(([k, v]) => (_jsx(Field, { label: k === "internalComment"
                                ? "Internal Only"
                                : k === "comment"
                                    ? "Customer Visible"
                                    : fieldLabel(k), children: _jsx("input", { value: v, onChange: (e) => field(k, e.target.value) }) }, k))), ["bags", "yellowBags", "soiledBags", "km"]
                            .filter((k) => k in payload)
                            .map((k) => (_jsx(Field, { label: fieldLabel(k), children: _jsx("input", { type: "number", min: "0", value: payload[k] ?? "", onChange: (e) => field(k, e.target.value === "" ? null : Number(e.target.value)) }) }, k)))] }), _jsx("div", { className: "form-grid", children: ["startedAt", "finishedAt", "arrival", "departure"]
                        .filter((k) => k in payload)
                        .map((k) => (_jsx(Field, { label: fieldLabel(k) + " — actual, local time", children: _jsx("input", { type: "datetime-local", value: localTime(payload[k]), onChange: (e) => field(k, e.target.value
                                ? new Date(e.target.value).toISOString()
                                : null) }) }, k))) }), payload.assistants && (_jsx(Assistants, { staff: staff.filter((u) => u.sites.includes("*") || u.sites.includes(row.site_id)), values: payload.assistants, onChange: (v) => field("assistants", v) })), row.kind === "receipt" && (_jsxs("section", { className: "subpanel", children: [_jsx("h3", { children: "Source count allocations" }), payload.allocations.map((a, n) => (_jsxs("div", { className: "form-grid", children: [_jsx(Field, { label: a.itemId + " · count " + a.countId.slice(0, 8) + " actual", children: _jsx("input", { type: "number", min: "0", value: a.quantity, onChange: (e) => {
                                            const lines = structuredClone(payload.allocations);
                                            lines[n].quantity = Number(e.target.value);
                                            field("allocations", lines);
                                        } }) }), _jsx(Field, { label: "Allocated estimate", children: _jsx("input", { type: "number", min: "0", value: a.estimatedQuantity, onChange: (e) => {
                                            const lines = structuredClone(payload.allocations);
                                            lines[n].estimatedQuantity = Number(e.target.value);
                                            field("allocations", lines);
                                        } }) })] }, a.countId + ":" + a.itemId))), _jsx(Field, { label: "Add missing source-count item", children: _jsxs("select", { value: "", onChange: (e) => {
                                    if (!e.target.value)
                                        return;
                                    const [id, itemId] = e.target.value.split("|"), source = rows.find((r) => r.id === id);
                                    field("allocations", [
                                        ...payload.allocations,
                                        {
                                            countId: id,
                                            countVersion: source.version,
                                            itemId,
                                            quantity: 0,
                                            estimatedQuantity: 0,
                                        },
                                    ]);
                                }, children: [_jsx("option", { value: "", children: "Select a source count/item" }), rows
                                        .filter((r) => r.kind === "count" &&
                                        r.customer_id === row.customer_id &&
                                        r.site_id === row.site_id &&
                                        r.payload.status.startsWith("Completed"))
                                        .flatMap((r) => r.payload.lines
                                        .filter((l) => !payload.allocations.some((a) => a.countId === r.id && a.itemId === l.itemId))
                                        .map((l) => (_jsxs("option", { value: r.id + "|" + l.itemId, children: [r.payload.locationId, " \u00B7 ", l.itemId, " \u00B7 count", " ", r.id.slice(0, 8)] }, r.id + "|" + l.itemId))))] }) }), _jsx("p", { children: "Zero releases an allocation. All quantity changes are checked against source availability and retain earlier receipt revisions." })] })), (payload.lines ?? []).map((l, n) => (_jsxs("div", { className: "subpanel", children: [_jsxs("h3", { children: [l.locationId ? l.locationId + " · " : "", l.itemId] }), _jsxs("div", { className: "form-grid", children: ["parts" in l ? (_jsx(Field, { label: "Count components", children: _jsx("input", { value: parts[l.itemId] ?? "", onChange: (e) => setParts({ ...parts, [l.itemId]: e.target.value }) }) })) : (_jsx(Field, { label: "Quantity", children: _jsx("input", { type: "number", min: "0", value: l.quantity ?? "", onChange: (e) => {
                                            const lines = structuredClone(payload.lines);
                                            lines[n].quantity =
                                                e.target.value === "" ? null : Number(e.target.value);
                                            field("lines", lines);
                                        } }) })), "estimated" in l && (_jsx(Field, { label: "Estimated septic contents", children: _jsx("input", { type: "number", min: "0", value: l.estimated ?? "", onChange: (e) => {
                                            const lines = structuredClone(payload.lines);
                                            lines[n].estimated =
                                                e.target.value === "" ? null : Number(e.target.value);
                                            field("lines", lines);
                                        } }) }))] })] }, n))), _jsx(Field, { label: "Mandatory correction reason", children: _jsx("input", { required: true, value: reason, onChange: (e) => setReason(e.target.value) }) }), error && _jsx("p", { role: "alert", children: error }), _jsxs("div", { className: "actions", children: [_jsx("button", { type: "button", onClick: close, children: "Cancel" }), _jsx("button", { className: "primary", children: "Save controlled revision" })] })] }) }));
}
createRoot(document.getElementById("root")).render(_jsx(App, {}));
